import { useEffect, useState } from "react";
import { RefreshCw, Users, Mail, Phone, MapPin, Save, Film, Upload, Trash2, Loader2, LogOut, CalendarDays, Crown, Home, Store, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken, clearToken } from "@/lib/site";
import { AdminLogin } from "@/components/site/AdminLogin";
import { AnalyticsPanel } from "@/components/site/AnalyticsPanel";

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [toolkit, setToolkit] = useState([]);
  const [fund, setFund] = useState({ goal: 5000, raised: 0, donor_count: 0 });
  const [raised, setRaised] = useState("");
  const [loading, setLoading] = useState(true);

  const [sermons, setSermons] = useState([]);
  const [sTitle, setSTitle] = useState("");
  const [sDesc, setSDesc] = useState("");
  const [sFile, setSFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [events, setEvents] = useState([]);
  const [eTitle, setETitle] = useState("");
  const [eDate, setEDate] = useState("");
  const [eLoc, setELoc] = useState("");
  const [eDesc, setEDesc] = useState("");
  const [eFile, setEFile] = useState(null);
  const [eUploading, setEUploading] = useState(false);
  const [eProgress, setEProgress] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c, d, m, v, tk] = await Promise.all([
        api.get("/interest"),
        api.get("/fundraiser"),
        api.get("/sermons"),
        api.get("/events"),
        api.get("/membership"),
        api.get("/vendors"),
        api.get("/toolkit"),
      ]);
      setList(a.data);
      setFund(b.data);
      setRaised(String(b.data.raised));
      setSermons(c.data);
      setEvents(d.data);
      setMembers(m.data);
      setVendors(v.data);
      setToolkit(tk.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        setAuthed(false);
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const verify = async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      try {
        await api.get("/auth/me");
        setAuthed(true);
      } catch {
        clearToken();
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const logout = () => {
    clearToken();
    setAuthed(false);
    toast.success("Signed out");
  };

  const saveRaised = async () => {
    try {
      const r = await api.put("/fundraiser", { raised: parseFloat(raised) || 0 });
      setFund(r.data);
      toast.success("Fundraiser updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const uploadSermon = async (e) => {
    e.preventDefault();
    if (!sTitle.trim() || !sFile) {
      toast.error("Please add a title and choose a video file.");
      return;
    }
    const fd = new FormData();
    fd.append("title", sTitle);
    fd.append("description", sDesc);
    fd.append("file", sFile);
    setUploading(true);
    setProgress(0);
    try {
      const r = await api.post("/sermons", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      setSermons((prev) => [r.data, ...prev]);
      setSTitle("");
      setSDesc("");
      setSFile(null);
      toast.success("Sermon uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const deleteSermon = async (id) => {
    try {
      await api.delete(`/sermons/${id}`);
      setSermons((prev) => prev.filter((s) => s.id !== id));
      toast.success("Sermon removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const uploadEvent = async (e) => {
    e.preventDefault();
    if (!eTitle.trim() || !eFile) {
      toast.error("Please add a title and choose a flyer image.");
      return;
    }
    const fd = new FormData();
    fd.append("title", eTitle);
    fd.append("event_date", eDate);
    fd.append("location", eLoc);
    fd.append("description", eDesc);
    fd.append("file", eFile);
    setEUploading(true);
    setEProgress(0);
    try {
      const r = await api.post("/events", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setEProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      setEvents((prev) => [r.data, ...prev]);
      setETitle("");
      setEDate("");
      setELoc("");
      setEDesc("");
      setEFile(null);
      toast.success("Event posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setEUploading(false);
      setEProgress(0);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
      toast.success("Event removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#06040A] flex items-center justify-center" data-testid="admin-checking">
        <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#06040A] text-[#F9F6F0] px-6 py-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display text-4xl gold-text font-bold">Ministry Dashboard</h1>
            <p className="text-[#A19CA8] text-sm mt-1">The Change Center Church Atlanta</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-all"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 border border-[#D4AF37]/40 text-[#A19CA8] px-4 py-2 text-sm hover:text-red-400 hover:border-red-400/40 transition-all"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <Stat label="Founding Members" value={members.length} icon={Crown} />
          <Stat label="Vendors" value={vendors.length} icon={Store} />
          <Stat label="Toolkit Requests" value={toolkit.length} icon={Rocket} />
          <Stat label="Interest Forms" value={list.length} icon={Users} />
          <Stat label="Raised" value={`$${fund.raised.toLocaleString()}`} icon={Save} />
        </div>

        <AnalyticsPanel />

        <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-6 mb-10">
          <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-3">Update Amount Raised</p>
          <div className="flex gap-3">
            <input
              value={raised}
              onChange={(e) => setRaised(e.target.value)}
              data-testid="admin-raised-input"
              type="number"
              className="ccc-input max-w-xs"
              placeholder="0"
            />
            <button
              onClick={saveRaised}
              data-testid="admin-save-raised"
              className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-6 py-2"
            >
              <Save className="h-4 w-4" /> Save
            </button>
          </div>
        </div>

        <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-6 mb-10">
          <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2">
            <Film className="h-4 w-4" /> Sermon Videos — Pastor Duane Thomas
          </p>
          <form onSubmit={uploadSermon} className="space-y-4" data-testid="sermon-upload-form">
            <input
              value={sTitle}
              onChange={(e) => setSTitle(e.target.value)}
              data-testid="sermon-title-input"
              placeholder="Sermon title (e.g., The Change Is Coming)"
              className="ccc-input"
            />
            <input
              value={sDesc}
              onChange={(e) => setSDesc(e.target.value)}
              data-testid="sermon-desc-input"
              placeholder="Short description (optional)"
              className="ccc-input"
            />
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
              onChange={(e) => setSFile(e.target.files?.[0] || null)}
              data-testid="sermon-file-input"
              className="ccc-input file:mr-4 file:border-0 file:bg-[#D4AF37] file:text-[#06040A] file:px-4 file:py-1.5 file:font-semibold file:uppercase file:text-xs file:cursor-pointer"
            />
            {uploading && (
              <div className="h-2 w-full bg-[#06040A] border border-[#D4AF37]/30 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3D56D] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
            <button
              type="submit"
              disabled={uploading}
              data-testid="sermon-upload-btn"
              className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-6 py-2.5 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? `Uploading ${progress}%` : "Upload Sermon"}
            </button>
          </form>

          {sermons.length > 0 && (
            <div className="mt-6 space-y-3">
              {sermons.map((s) => (
                <div key={s.id} data-testid="admin-sermon" className="flex items-center justify-between gap-4 bg-[#06040A] border border-[#D4AF37]/20 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-[#F9F6F0] font-medium truncate">{s.title}</p>
                    <p className="text-[#A19CA8] text-xs truncate">
                      {(s.size / (1024 * 1024)).toFixed(1)} MB · {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteSermon(s.id)}
                    data-testid={`delete-sermon-${s.id}`}
                    className="text-[#A19CA8] hover:text-red-400 transition-colors shrink-0"
                    aria-label="Delete sermon"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-6 mb-10">
          <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2">
            <CalendarDays className="h-4 w-4" /> Events — Upload Flyers
          </p>
          <form onSubmit={uploadEvent} className="space-y-4" data-testid="event-upload-form">
            <input
              value={eTitle}
              onChange={(e) => setETitle(e.target.value)}
              data-testid="event-title-input"
              placeholder="Event title (e.g., Forest Park City Wide One Day Revival)"
              className="ccc-input"
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                value={eDate}
                onChange={(e) => setEDate(e.target.value)}
                data-testid="event-date-input"
                placeholder="Date & time (e.g., Sat, Aug 22, 2026 · 2–6pm)"
                className="ccc-input"
              />
              <input
                value={eLoc}
                onChange={(e) => setELoc(e.target.value)}
                data-testid="event-location-input"
                placeholder="Location (e.g., Starr Park, Forest Park, GA)"
                className="ccc-input"
              />
            </div>
            <textarea
              value={eDesc}
              onChange={(e) => setEDesc(e.target.value)}
              data-testid="event-desc-input"
              rows={2}
              placeholder="Short description (optional)"
              className="ccc-input resize-none"
            />
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
              onChange={(e) => setEFile(e.target.files?.[0] || null)}
              data-testid="event-file-input"
              className="ccc-input file:mr-4 file:border-0 file:bg-[#D4AF37] file:text-[#06040A] file:px-4 file:py-1.5 file:font-semibold file:uppercase file:text-xs file:cursor-pointer"
            />
            {eUploading && (
              <div className="h-2 w-full bg-[#06040A] border border-[#D4AF37]/30 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3D56D] transition-all" style={{ width: `${eProgress}%` }} />
              </div>
            )}
            <button
              type="submit"
              disabled={eUploading}
              data-testid="event-upload-btn"
              className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-6 py-2.5 disabled:opacity-60"
            >
              {eUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {eUploading ? `Uploading ${eProgress}%` : "Post Event"}
            </button>
          </form>

          {events.length > 0 && (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((ev) => (
                <div key={ev.id} data-testid="admin-event" className="bg-[#06040A] border border-[#D4AF37]/20 overflow-hidden">
                  <img src={`${API}/events/${ev.id}/image`} alt={ev.title} className="w-full h-36 object-cover" />
                  <div className="p-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[#F9F6F0] text-sm font-medium truncate">{ev.title}</p>
                      {ev.event_date && <p className="text-[#A19CA8] text-xs truncate">{ev.event_date}</p>}
                    </div>
                    <button
                      onClick={() => deleteEvent(ev.id)}
                      data-testid={`delete-event-${ev.id}`}
                      className="text-[#A19CA8] hover:text-red-400 transition-colors shrink-0"
                      aria-label="Delete event"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <h2 className="font-display text-2xl text-[#D4AF37] mb-4 flex items-center gap-2">
          <Rocket className="h-5 w-5" /> Turnaround Toolkit Requests
        </h2>
        {toolkit.length === 0 ? (
          <p className="text-[#A19CA8] mb-10">No toolkit requests yet.</p>
        ) : (
          <div className="space-y-4 mb-12">
            {toolkit.map((t) => (
              <div key={t.id} data-testid="admin-toolkit" className="bg-[#130A1F]/60 border border-[#9B6DFF]/25 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="font-display text-xl text-[#F9F6F0]">{t.full_name} <span className="text-[#A19CA8] text-base">· {t.city}, {t.state}</span></h3>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 border ${t.payment_status === "paid" ? "border-green-500/50 text-green-400" : "border-[#D4AF37]/40 text-[#D4AF37]"}`}>
                      {t.payment_status === "paid" ? "PAID" : "PAYMENT PENDING"}
                    </span>
                    <span className="text-xs text-[#A19CA8]">{new Date(t.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#A19CA8] mb-2">
                  <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#9B6DFF]" />{t.email}</span>
                  <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#9B6DFF]" />{t.phone}</span>
                  <span><span className="text-[#9B6DFF]">Type:</span> {t.org_type}</span>
                  {t.organization && <span><span className="text-[#9B6DFF]">Org:</span> {t.organization}</span>}
                  {t.role && <span><span className="text-[#9B6DFF]">Role:</span> {t.role}</span>}
                  {t.youth_count && <span><span className="text-[#9B6DFF]">Youth:</span> {t.youth_count}</span>}
                </div>
                {t.message && <p className="text-[#A19CA8] text-sm italic mt-2">"{t.message}"</p>}
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display text-2xl text-[#D4AF37] mb-4 flex items-center gap-2">
          <Store className="h-5 w-5" /> Vendor Registrations — Aug 22 Event
        </h2>
        {vendors.length === 0 ? (
          <p className="text-[#A19CA8] mb-10">No vendor registrations yet.</p>
        ) : (
          <div className="space-y-4 mb-12">
            {vendors.map((v) => (
              <div key={v.id} data-testid="admin-vendor" className="bg-[#130A1F]/60 border border-[#D4AF37]/20 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="font-display text-xl text-[#F9F6F0]">{v.business_name}</h3>
                  <span className={`text-xs px-2.5 py-1 border ${v.payment_status === "paid" ? "border-green-500/50 text-green-400" : "border-[#D4AF37]/40 text-[#D4AF37]"}`}>
                    {v.payment_status === "paid" ? "PAID" : "PAYMENT PENDING"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#A19CA8] mb-3">
                  <span>{v.contact_name}</span>
                  <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#D4AF37]" />{v.email}</span>
                  <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#D4AF37]" />{v.phone}</span>
                  <span><span className="text-[#D4AF37]">Pay via:</span> {v.payment_method}</span>
                </div>
                {v.categories?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {v.categories.map((c) => (
                      <span key={c} className="text-xs border border-[#D4AF37]/40 text-[#D4AF37] px-2.5 py-1">{c}</span>
                    ))}
                  </div>
                )}
                <p className="text-[#A19CA8] text-sm mt-2">{v.product_description}</p>
                {v.food_type && <p className="text-[#A19CA8] text-sm mt-1"><span className="text-[#D4AF37]">Food/Bev:</span> {v.food_type}</p>}
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#A19CA8] mt-2">
                  {v.website && <span><span className="text-[#D4AF37]">Web:</span> {v.website}</span>}
                  {v.social_media && <span><span className="text-[#D4AF37]">Social:</span> {v.social_media}</span>}
                  {v.special_requests && <span><span className="text-[#D4AF37]">Requests:</span> {v.special_requests}</span>}
                </div>
                <p className="text-xs text-[#A19CA8]/60 mt-2">{new Date(v.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display text-2xl text-[#D4AF37] mb-4 flex items-center gap-2">
          <Crown className="h-5 w-5" /> Founding Member Applications
        </h2>
        {members.length === 0 ? (
          <p className="text-[#A19CA8] mb-10">No membership applications yet.</p>
        ) : (
          <div className="space-y-4 mb-12">
            {members.map((m) => (
              <div key={m.id} data-testid="admin-member" className="bg-[#130A1F]/60 border border-[#D4AF37]/20 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="font-display text-xl text-[#F9F6F0]">{m.full_name}</h3>
                  <span className="text-xs text-[#A19CA8]">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#A19CA8] mb-3">
                  <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#D4AF37]" />{m.email}</span>
                  <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#D4AF37]" />{m.phone}</span>
                  <span className="flex items-center gap-1.5"><Home className="h-4 w-4 text-[#D4AF37]" />{m.address}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm text-[#A19CA8] mb-3">
                  {m.date_of_birth && <span><span className="text-[#D4AF37]">DOB:</span> {m.date_of_birth}</span>}
                  {m.marital_status && <span><span className="text-[#D4AF37]">Status:</span> {m.marital_status}</span>}
                  {m.spouse_name && <span><span className="text-[#D4AF37]">Spouse:</span> {m.spouse_name}</span>}
                  {m.children && <span><span className="text-[#D4AF37]">Family:</span> {m.children}</span>}
                  {m.accepted_christ && <span><span className="text-[#D4AF37]">Accepted Christ:</span> {m.accepted_christ}</span>}
                  {m.baptized && <span><span className="text-[#D4AF37]">Baptized:</span> {m.baptized}</span>}
                  {m.previous_church && <span><span className="text-[#D4AF37]">Prev. Church:</span> {m.previous_church}</span>}
                  {m.how_heard && <span><span className="text-[#D4AF37]">Heard via:</span> {m.how_heard}</span>}
                </div>
                {m.ministries?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {m.ministries.map((r) => (
                      <span key={r} className="text-xs border border-[#D4AF37]/40 text-[#D4AF37] px-2.5 py-1">{r}</span>
                    ))}
                  </div>
                )}
                {m.prayer_request && <p className="text-[#A19CA8] text-sm italic mt-2">Prayer: "{m.prayer_request}"</p>}
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display text-2xl text-[#D4AF37] mb-4">Interest Submissions</h2>
        {loading ? (
          <p className="text-[#A19CA8]">Loading…</p>
        ) : list.length === 0 ? (
          <p className="text-[#A19CA8]">No submissions yet.</p>
        ) : (
          <div className="space-y-4">
            {list.map((s) => (
              <div key={s.id} data-testid="admin-submission" className="bg-[#130A1F]/60 border border-[#D4AF37]/20 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="font-display text-xl text-[#F9F6F0]">{s.full_name}</h3>
                  <span className="text-xs text-[#A19CA8]">{new Date(s.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#A19CA8] mb-3">
                  <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#D4AF37]" />{s.email}</span>
                  <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#D4AF37]" />{s.phone}</span>
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#D4AF37]" />{s.city}</span>
                </div>
                {s.roles?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {s.roles.map((r) => (
                      <span key={r} className="text-xs border border-[#D4AF37]/40 text-[#D4AF37] px-2.5 py-1">
                        {r}
                      </span>
                    ))}
                  </div>
                )}
                {s.message && <p className="text-[#A19CA8] text-sm italic mt-2">"{s.message}"</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-6">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-widest text-[#A19CA8]">{label}</p>
      <Icon className="h-5 w-5 text-[#D4AF37]" />
    </div>
    <p className="font-display text-3xl gold-text font-bold mt-2">{value}</p>
  </div>
);
